/**
 * The typed reason an operator write carries (work order G4, AC-G4c).
 *
 * JSX-free so the rules are testable in the dashboard's node-only Jest. The bounds are the API's
 * `OperatorReasonDto` — 10 to 500 characters, counted AFTER trimming, which the API now does too:
 * ten spaces were a reason until it did.
 *
 * Ticket replies, notes and status changes do NOT come through here. They keep their generated
 * reason (`support-desk.md` § Two audit trails): the ticket already says why, and a mandatory box
 * on every status change would only ever be filled with ".".
 */

import { isReasonAcceptable, REASON_MIN_LENGTH } from './shop-file';

/** Upper bound of a typed reason — the API's `@MaxLength(500)`. */
export const REASON_MAX_LENGTH = 500;

/** Where a typed reason stands, so the prompt can say which rule it breaks. */
export type ReasonState = 'empty' | 'short' | 'long' | 'ok';

/**
 * Classifies a draft reason.
 *
 * @param reason - What the operator typed.
 */
export function reasonState(reason: string): ReasonState {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return 'empty';
  if (!isReasonAcceptable(trimmed)) return 'short';
  if (trimmed.length > REASON_MAX_LENGTH) return 'long';
  return 'ok';
}

/** Whether a draft reason may be sent. */
export function canSubmitReason(reason: string): boolean {
  return reasonState(reason) === 'ok';
}

/** How many more characters a short reason needs, for the hint under the box. */
export function reasonCharactersMissing(reason: string): number {
  return Math.max(0, REASON_MIN_LENGTH - reason.trim().length);
}

/** The request body for an audited write. Trimmed, like the API stores it. */
export function operatorReason(reason: string): { reason: string } {
  return { reason: reason.trim() };
}

/**
 * The audit-log link for a row a write just recorded, or null when there is none to link.
 *
 * `null` from the API means a BEST-EFFORT row could not be written — the action still ran. The
 * caller says so rather than showing nothing, because "done, but not in the audit log" is
 * something the operator should know and may want to note elsewhere.
 */
export function auditEntryPath(
  auditLogId: string | null | undefined,
): string | null {
  return auditLogId
    ? `/audit-log?entry=${encodeURIComponent(auditLogId)}`
    : null;
}
